import { useQueryClient } from '@tanstack/react-query';

import {
  useListWorkflows,
  WorkflowDtoStatus,
  getListWorkflowsQueryKey,
  WorkflowResponse
} from '@/api/generated';
import { LocalCapabilityType } from '@/components/AiWorkflow/formSchema';
import { useWorkflow } from '@/components/AiWorkflow/hooks/useWorkflow';

export const useMyCapabilities = () => {
  const { patchWorkflow } = useWorkflow();
  const queryClient = useQueryClient();
  const listWorkflowsQueryKey = getListWorkflowsQueryKey();

  const handleToggle = (id: string | undefined, isActive: boolean) => {
    if (!id) return;
    const oldQueryData = queryClient.getQueryData<WorkflowResponse>(listWorkflowsQueryKey);
    queryClient.setQueryData(listWorkflowsQueryKey, (old: WorkflowResponse) =>
      old
        ? {
            ...old,
            items: old.items?.map(workflow =>
              workflow.id === id
                ? {
                    ...workflow,
                    status: isActive ? WorkflowDtoStatus.enabled : WorkflowDtoStatus.disabled
                  }
                : workflow
            )
          }
        : old
    );

    patchWorkflow(
      {
        id,
        data: {
          status: isActive ? WorkflowDtoStatus.enabled : WorkflowDtoStatus.disabled
        }
      },
      {
        onError: () => {
          queryClient.setQueryData(listWorkflowsQueryKey, oldQueryData);
        }
      }
    );
  };

  const { data: workflows } = useListWorkflows();

  const myAgents: LocalCapabilityType[] = (workflows?.items ?? [])
    .map(workflow => ({
      id: workflow.id,
      title: workflow.title,
      status: workflow.status,
      prompt: workflow.flow?.prompt,
      trigger: workflow.trigger?.scheduled?.daysOfWeek ?? [],
      scope: workflow.context?.meetings?.scope ?? undefined,
      timeRange: workflow.context?.meetings?.timeRange,
      keyWords: workflow.context?.meetings?.keywords,
      notifications: { email: workflow.notifications?.email?.type === 'email' },
      createdAt: workflow.createdAt,
      updatedAt: workflow.updatedAt,
      createdFrom: workflow.createdFrom,
      googleDrive: workflow.context?.googleDrive?.files
        ? {
            files: workflow.context.googleDrive.files
              .filter(file => file.fileId && file.fileName)
              .map(file => ({
                fileId: file.fileId!,
                fileName: file.fileName!
              }))
          }
        : { files: [] }
    }))
    .sort((a, b) => new Date(b.updatedAt ?? '').getTime() - new Date(a.updatedAt ?? '').getTime());

  return {
    myAgents,
    handleToggle
  };
};
